import React, {useContext, useState} from "react";
import {
    Button,
    Stack,
    Toolbar,
    Typography,
    AppBar,
    IconButton,
    Tooltip,
    Menu,
    Box,
    CircularProgress, ListItemButton, List, ListItemIcon, ListItemText, Divider, alpha
} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import Link from '@mui/material/Link';
import {HugeiconsIcon} from "@hugeicons/react";
import {
    ArrowDown01Icon,
    ArrowLeft04Icon,
    Cancel01Icon,
    ChatBotIcon,
    CheckListIcon,
    ClipboardCopyIcon,
    File01Icon,
    FileExportIcon,
    HeadsetIcon,
    Menu01Icon,
    PhoneCallIcon,
    PlayIcon,
    Rocket01Icon,
    ArrowLeft02Icon,
} from "@hugeicons/core-free-icons";
import {useSetAtom} from "jotai";
import {Context as AppContext, ProjectRoles} from "../../../context/AppContext";
import {StartCallAtom} from "../../../pages/project/atoms";
import {HeaderDrawer} from "./HeaderDrawer";
import {toast} from "react-toastify";
import {copyToClipboard} from "../../../utils/functions";
import {FlowProjectRepository} from "../../../repositories/FlowProjectRepository";
import axios from "axios";
import useConfirm from "../../../hooks/useConfirm";
import {SignInContext} from "../../../context/SignInContext";
import {NotificationButton} from "./notification-button";
import {grey} from "@mui/material/colors";
import {SaveIcon} from "@hugeicons/core-free-icons";

const START_MENU_ITEMS = [
    {key: "chatbot", label: "Chatbot", description: "Open the chat test drawer", icon: ChatBotIcon},
    {key: "web", label: "Web Call", description: "Talk to it over your mic", icon: HeadsetIcon},
    {key: "phone", label: "Phone Call", description: "Ring a real phone number", icon: PhoneCallIcon},
];

// Header equivalent of the Start node's dropdown (see StartNode.js) — "Chatbot" reuses the
// existing toggleChat/ChatDrawerAtom flow (entryNode as the start point), "Web Call"/"Phone Call"
// open StartCallModal (mounted in FlowLayout) the same way the node's menu does.
const HeaderStartButton = ({project, openChat, toggleChat, loading, dense = false}) => {
    const setStartCall = useSetAtom(StartCallAtom);
    const [anchorEl, setAnchorEl] = useState(null);

    if (openChat) {
        return (
            <Button
                variant={"ghost"}
                color={"secondary"}
                size={"small"}
                disabled={!project || loading}
                onClick={toggleChat}
                startIcon={<HugeiconsIcon icon={Cancel01Icon} size={dense ? 15 : 17}/>}
                sx={{
                    borderRadius: 2, minWidth: 40, px: dense ? 1.5 : 2,
                    border: `1px solid ${grey[300]}`,
                    color: "text.secondary",
                }}
            >
                Close
            </Button>
        );
    }

    const onSelect = (key) => {
        setAnchorEl(null);
        if (key === "chatbot") {
            toggleChat();
        } else {
            setStartCall({open: true, mode: key});
        }
    };

    return (
        <>
            <Button
                variant={"primary"}
                color={"primary"}
                size={"small"}
                disabled={!project || loading}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={<HugeiconsIcon icon={PlayIcon} size={dense ? 14 : 16} strokeWidth={2}/>}
                endIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={14} style={{transform: anchorEl ? "rotate(180deg)" : "none", transition: "transform .16s ease"}}/>}
                sx={{
                    borderRadius: 2, minWidth: 40, px: dense ? 1.5 : 2,
                    boxShadow: "0 2px 6px rgba(132, 145, 247, 0.45)",
                    color: "primary.contrastText",
                }}
            >
                Start
            </Button>
            <Menu
                component={List}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                slotProps={{paper: {sx: {p: 1, borderRadius: 3, minWidth: 230}}}}
            >
                {START_MENU_ITEMS.map(({key, label, description, icon: Icon}) => (
                    <ListItemButton key={key} onClick={() => onSelect(key)} sx={{borderRadius: 1.5, mb: 0.25}}>
                        <ListItemIcon sx={{minWidth: 34}}>
                            <Box sx={{
                                width: 30, height: 30, borderRadius: "9px", display: "grid", placeItems: "center",
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                color: "primary.main",
                            }}>
                                <HugeiconsIcon icon={Icon} size={17} strokeWidth={1.8}/>
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={label}
                            secondary={description}
                            primaryTypographyProps={{fontSize: 14}}
                            secondaryTypographyProps={{fontSize: 11.5}}
                        />
                    </ListItemButton>
                ))}
            </Menu>
        </>
    );
};

const checkNodesValid = (project) => {
    // TODO: implement real validation of node.data
    return true;
};


const pillButtonSx = {
    minWidth: 0,
    px: 1.5,
    height: 34,
    borderRadius: 1.75,
    color: "text.secondary",
    fontSize: 13,
    fontWeight: 500,
    transition: "background-color .15s ease, box-shadow .15s ease, color .15s ease",
    "&:hover": {
        bgcolor: grey[100],
        color: "text.primary",
    },
};

export const Header = ({project, openChat, toggleChat}) => {
    const {publishVersion} = useContext(AppContext);
    const {currentUser} = useContext(SignInContext);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [loading, setLoading] = useState(false);
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const [lastCheckPassed, setLastCheckPassed] = useState(true);
    const confirmDeploy = useConfirm(
        'Are you sure you want to deploy to production?',
    );

    const onCheck = () => {
        const isValid = checkNodesValid(project);
        setLastCheckPassed(isValid);
        if (isValid) {
            toast.success("All nodes passed validation.");
        } else {
            toast.error("Some nodes have validation errors.");
        }
        return isValid;
    }

    const onPublish = async () => {
        if (!checkNodesValid(project)) {
            setLastCheckPassed(false);
            toast.error("Fix validation errors before saving.");
            return;
        }
        try {
            setLoading(true);
            await publishVersion(project);
            const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
            const projectVersionResponse = await axios.get(fetchVersion)
            await FlowProjectRepository.updateFlow({id: project?.id, project: projectVersionResponse?.data || null})
        } catch (e) {
            console.log(e);
            toast.error("Error! Please try again.")
        } finally {
            setLoading(false);
        }
    }

    const onDeploy = async () => {
        if (!project?.settings?.deploymentProject) {
            return;
        }
        if (!checkNodesValid(project)) {
            setLastCheckPassed(false);
            toast.error("Fix validation errors before deploying.");
            return;
        }
        const result = await confirmDeploy();
        if (!result) {
            return;
        }
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/deployProject`
            const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
            const response = await axios.post(url, {
                fromProjectId: project?.id,
                toProjectId: project?.settings?.deploymentProject,
            })

            const projectVersionResponse = await axios.get(fetchVersion)
            if (response?.data?.project || response?.project) {
                const newProject = response?.data?.project || response?.project;
                await publishVersion(newProject || null);
                await FlowProjectRepository.updateProjectFromStage({
                    id: project?.settings?.deploymentProject,
                    url: newProject?.configuration?.conversation_logger_api || null,
                    project: projectVersionResponse?.data || null
                })
            }
        } catch (e) {
            console.log(e);
            toast.error("Error! Please try again.")
        } finally {
            setLoading(false);
        }
    }

    const userRole = currentUser?.roles?.includes("admin") ? ProjectRoles.ADMIN : project?.members?.find(m => m?.username === currentUser?.username)?.role;

    const closeActions = () => setActionsAnchorEl(null);

    const copyProjectUrl = async () => {
        try {
            const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`;
            await copyToClipboard(url);
            toast.success("URL is copied successfully.");
        } catch (e) {
            console.log(e);
            toast.error("URL failed to copy.");
        } finally {
            closeActions();
        }
    };

    const downloadProjectFile = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/export/${project.id}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `${project.id}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Error downloading file: ", e);
            toast.error("File download failed.");
        } finally {
            closeActions();
        }
    };

    return (
        <>
            <HeaderDrawer
                open={openDrawer}
                handleClose={() => setOpenDrawer(false)}
                project={project}
                userRole={userRole}
                loading={loading}
                onCheck={onCheck}
                onPublish={onPublish}
                onDeploy={onDeploy}
            />
            <AppBar position="static"
                    sx={{
                        bgcolor: "#fefefe",
                        boxShadow: 0,
                        zIndex: 100,
                        borderBottom: 1,
                        minHeight: "48px !important",
                        borderColor: "divider"
                    }}
            >
                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        display: {xs: "none", md: "flex"},
                        zIndex: 100,
                        py: "0 !important",
                        px: "8px !important",
                        minHeight: "48px !important",
                    }}
                >
                    <Box sx={{
                        width: "100%",
                        display: "flex",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2
                    }}>
                        <Stack direction={"row"} gap={0.5} alignItems={"center"} sx={{minWidth: 0}}>
                            <Button
                                component={RouterLink}
                                to={"/projects"}
                                variant={"ghost"}
                                sx={{flexDirection: "row", color: "dark.main", minWidth: 0, px: 0.5}}
                                startIcon={<img src={"/logo.svg"} alt={"logo"} width={36}/>}
                            />

                            <Tooltip title="Back to projects">
                                <IconButton
                                    component={RouterLink}
                                    to="/projects"
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: 1.75,
                                        color: "text.secondary",
                                        "&:hover": {bgcolor: grey[100], color: "text.primary"},
                                    }}
                                >
                                    <HugeiconsIcon icon={ArrowLeft02Icon} size={24}/>
                                </IconButton>
                            </Tooltip>
                            <Typography
                                fontSize={14}
                                fontWeight={600}
                                color="text.primary"
                                noWrap
                                sx={{maxWidth: {md: 260, lg: 420}}}
                            >
                                {project?.name}
                            </Typography>
                            {/* Dirty-state indicator — wire this to real "has unsaved
                  changes" state once that's tracked; purely decorative
                  for now. */}
                            {/* <Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "warning.main"}} /> */}
                        </Stack>

                        <Stack component="nav" aria-label="Project actions" direction={"row"} gap={0.75} alignItems={"center"} sx={{justifySelf: "end", flexShrink: 0}}>
                            {/*<DialButton project={project} />*/}
                            {
                                userRole !== ProjectRoles.AGENT ?
                                    <>
                                        <Stack direction={"row"} alignItems={"center"} gap={0.25}>
                                            <ExportProjectButton
                                              project={project}
                                              showIconText={false}
                                              size={"small"}
                                              sx={{
                                                  color: "text.secondary",
                                                  minWidth: 0,
                                                  width: 34,
                                                  height: 34,
                                                  borderRadius: 1.75,
                                                  "&:hover": {bgcolor: grey[100], color: "text.primary"},
                                              }}
                                            />
                                            <Divider
                                              orientation="vertical"
                                              flexItem
                                              sx={{ height: '18px', alignSelf: 'center', borderColor: grey[300], mx: 0.25 }}
                                            />
                                            <ProjectActionsButton
                                                project={project}
                                                loading={loading}
                                                lastCheckPassed={lastCheckPassed}
                                                onCheck={onCheck}
                                                onPublish={onPublish}
                                                onDeploy={onDeploy}
                                                sx={pillButtonSx}
                                            />
                                        </Stack>

                                        <NotificationButton project={project}/>
                                    </> : null
                            }
                            {userRole !== ProjectRoles.AGENT && <Divider orientation="vertical" flexItem sx={{height: 26, alignSelf: "center", borderColor: "#e5e7eb"}} />}
                            {/* Rolled back to a dropdown (Chatbot/Web Call/Phone Call) — restore this plain
                                toggle button and delete the <HeaderStartButton/> below to revert.
                            <Button
                                variant={openChat ? "ghost" : "primary"}
                                color={openChat ? "secondary" : "primary"}
                                size={"small"}
                                disabled={!project || loading}
                                onClick={toggleChat}
                    startIcon={openChat ? <HugeiconsIcon icon={Cancel01Icon} size={17}/> : <HugeiconsIcon icon={PlayIcon} size={16} strokeWidth={2}/>}
                                sx={{
                                    borderRadius: 2, minWidth: 40, px: 2,
                                    boxShadow: openChat ? "none" : "0 2px 6px rgba(132, 145, 247, 0.45)",
                                    border: openChat ? `1px solid ${grey[300]}` : "none",
                                    color: openChat ? "text.secondary" : "primary.contrastText",
                                }}
                            >
                                {openChat ? "Close" : "Start"}
                            </Button>
                            */}
                            <HeaderStartButton project={project} openChat={openChat} toggleChat={toggleChat} loading={loading} />
                        </Stack>
                    </Box>
                </Toolbar>

                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        display: {xs: "flex", md: "none"},
                        px: "8px !important",
                        minHeight: "48px !important",
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={1} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <Stack direction={"row"} gap={1} alignItems={"center"} sx={{minWidth: 0}}>
                            <Button
                                component={Link}
                                to={"/projects"}
                                variant={"ghost"}
                                sx={{flexDirection: "row", color: "dark.main", minWidth: 0, px: 0.5, flexShrink: 0}}
                            >
                                <img src={"/logo.svg"} alt={"logo"} width={26}/>
                            </Button>
                            <Typography
                                fontSize={14}
                                fontWeight={600}
                                color="text.primary"
                                noWrap
                                sx={{minWidth: 0}}
                            >
                                {project?.name}
                            </Typography>
                        </Stack>

                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            {/* Rolled back to a dropdown (Chatbot/Web Call/Phone Call) — restore this plain
                                toggle button and delete the <HeaderStartButton/> below to revert.
                            <Button
                                variant={openChat ? "ghost" : "primary"}
                                color={openChat ? "secondary" : "primary"}
                                size={"small"}
                                disabled={!project || loading}
                                onClick={toggleChat}
                    startIcon={openChat ? <HugeiconsIcon icon={Cancel01Icon} size={15}/> : <HugeiconsIcon icon={PlayIcon} size={14} strokeWidth={2}/>}
                                sx={{
                                    borderRadius: 2, minWidth: 40, px: 1.5,
                                    boxShadow: openChat ? "none" : "0 2px 6px rgba(132, 145, 247, 0.45)",
                                    border: openChat ? `1px solid ${grey[300]}` : "none",
                                    color: openChat ? "text.secondary" : "primary.contrastText",
                                }}
                            >
                                {openChat ? "Close" : "Start"}
                            </Button>
                            */}
                            <HeaderStartButton project={project} openChat={openChat} toggleChat={toggleChat} loading={loading} dense />
                            <IconButton onClick={() => setOpenDrawer(true)}>
                            <HugeiconsIcon icon={Menu01Icon}/>
                            </IconButton>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>
        </>
    )
}


// Groups Validate, Save and Deploy behind one dropdown button, mirroring the
// ExportProjectButton pattern next to it in the toolbar.
export const ProjectActionsButton = ({project, loading, lastCheckPassed, onCheck, onPublish, onDeploy, sx}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const hasDeployment = !!project?.settings?.deploymentProject;

    const handleOpen = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const runAndClose = (fn) => (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        fn?.();
        handleClose();
    };

    return (
        <>
            <Tooltip title="Validate, save or deploy this project">
                <Button
                    variant={"text"} size={"small"}
                    disabled={!project || loading}
                    onClick={handleOpen}
                    startIcon={loading ?
                        <CircularProgress size={15} sx={{color: grey[500]}}/> :
                        <HugeiconsIcon icon={SaveIcon} size={17} stroke={1.5} />}
                    endIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={15}/>}
                    sx={sx}
                >
                    Save
                </Button>
            </Tooltip>

            <Menu
                component={List}
                id="project-actions-menu"
                anchorEl={anchorEl}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                keepMounted
                transformOrigin={{vertical: 'top', horizontal: 'center'}}
                slotProps={{paper: {sx: {p: 1, borderRadius: 3, minWidth: 210}}}}
                open={open}
                onClose={handleClose}
            >
                <ListItemButton onClick={runAndClose(onCheck)} sx={{borderRadius: 1.5, mb: 0.25}}>
                    <ListItemIcon sx={{minWidth: 34}}><HugeiconsIcon icon={CheckListIcon} size={19} strokeWidth={1.5}/></ListItemIcon>
                    <ListItemText primary={"Validate"} primaryTypographyProps={{fontSize: 14}}/>
                    <Tooltip title={lastCheckPassed ? "Last check passed" : "Last check failed"}>
                        <Box sx={{
                            width: 7, height: 7, borderRadius: "50%", ml: 1, flexShrink: 0,
                            bgcolor: lastCheckPassed ? "success.main" : "error.main"
                        }}/>
                    </Tooltip>
                </ListItemButton>
                <ListItemButton onClick={runAndClose(onPublish)} sx={{borderRadius: 1.5, mb: hasDeployment ? 0.25 : 0}}>
                    <ListItemIcon sx={{minWidth: 34}}><HugeiconsIcon icon={SaveIcon} size={19} stroke={1.5} /></ListItemIcon>
                    <ListItemText primary={"Save"} primaryTypographyProps={{fontSize: 14}}/>
                </ListItemButton>
                {hasDeployment &&
                    <ListItemButton onClick={runAndClose(onDeploy)} sx={{borderRadius: 1.5}}>
                        <ListItemIcon sx={{minWidth: 34}}><HugeiconsIcon icon={Rocket01Icon} size={19} strokeWidth={1.5}/></ListItemIcon>
                        <ListItemText primary={"Deploy"} primaryTypographyProps={{fontSize: 14}}/>
                    </ListItemButton>
                }
            </Menu>
        </>
    )
}


export const ExportProjectButton = ({project, showIconText = false, ...props}) => {
    const [anchorElUser, setAnchorElUser] = React.useState(null);

    const handleOpenUserMenu = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorElUser(null);
    };

    const onCopyExport = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        if (project.id) {
            try {
                const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
                await copyToClipboard(url);
                toast.success("URL is copied successfully.")
            } catch (e) {
                console.log(e)
                toast.error("URL failed to copy.")
            }
        }

        handleCloseUserMenu(event);
    }
    const onExportFile = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        // Replace with the actual API endpoint that serves the file
        const apiUrl = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/export/${project?.id}`;

        fetch(apiUrl)
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);

                // Create an anchor element to trigger the download
                const a = document.createElement('a');
                a.href = url;
                a.download = `${project.id}.json`; // Specify the desired file name
                document.body.appendChild(a);
                a.click();

                // Clean up by removing the anchor and the URL
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error('Error downloading file: ', error);
            });

        handleCloseUserMenu(event);
    };


    return (
        <>
            {
                showIconText ?
                    <Tooltip title="Export">
                        <Button
                            onClick={handleOpenUserMenu}
                            color={"secondary"}
                            startIcon={
                                showIconText ? <HugeiconsIcon icon={FileExportIcon} size={18} stroke={1.5} /> : null
                            }
                            {...props}
                        >
                            Export
                        </Button>
                    </Tooltip> :
                    <Tooltip title="Export">
                        <IconButton
                            onClick={handleOpenUserMenu}
                            {...props}
                        >
                            <HugeiconsIcon icon={FileExportIcon} size={17} strokeWidth={1.5}/>
                        </IconButton>
                    </Tooltip>
            }

            <Menu
                component={List}
                // sx={{ p: 0, borderRadius: 0, minWidth: "200px"}}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            p: 1, borderRadius: 3,
                        }
                    }
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <ListItemButton onClick={onCopyExport} sx={{borderRadius: 1.5, mb: 0.25}}>
                    <ListItemIcon sx={{minWidth: 34}}><HugeiconsIcon icon={ClipboardCopyIcon} size={19} strokeWidth={1.5}/></ListItemIcon>
                    <ListItemText primary={"Copy URL"} primaryTypographyProps={{fontSize: 14}}/>
                </ListItemButton>
                <ListItemButton onClick={onExportFile} sx={{borderRadius: 1.5}}>
                    <ListItemIcon sx={{minWidth: 34}}><HugeiconsIcon icon={File01Icon} size={19} strokeWidth={1.5}/></ListItemIcon>
                    <ListItemText primary={"Download File"} primaryTypographyProps={{fontSize: 14}}/>
                </ListItemButton>
            </Menu>
        </>
    )
}
